/**
 * Cliente REST para o backend FastAPI do ACORDEI !.
 *
 * Substitui o tRPC original do Manus (que dependia do backend Express).
 * Todas as chamadas usam fetch via proxy do Vite (`/api/*` → http://localhost:8000).
 *
 * Em produção, configurar `VITE_API_BASE` no .env do build:
 *   VITE_API_BASE=https://api.acordei.app.br/api/v1
 *
 * Schema das respostas: bate com `backend/acordei_api/routes/*` (FastAPI + Pydantic).
 */

const BASE_URL: string =
  (import.meta as unknown as { env?: { VITE_API_BASE?: string } }).env?.VITE_API_BASE ??
  "/api/v1";

// ============================================================================
// Token de sessão (persistido em localStorage)
// ============================================================================

const _TOKEN_KEY = "acordei-session-token";

/** Lê o token da sessão atual. Retorna null se não houver. */
export function getSessionToken(): string | null {
  try {
    return localStorage.getItem(_TOKEN_KEY);
  } catch {
    return null;
  }
}

/** Persiste o token retornado pelo `/auth/verify`. */
export function setSessionToken(token: string | null): void {
  try {
    if (token) localStorage.setItem(_TOKEN_KEY, token);
    else localStorage.removeItem(_TOKEN_KEY);
  } catch {
    /* private mode / quota — ignorar */
  }
}

export class ApiError extends Error {
  constructor(public status: number, public detail: string, public raw?: unknown) {
    super(`API ${status}: ${detail}`);
  }
}

async function request<T>(
  metodo: "GET" | "POST" | "DELETE" | "PUT" | "PATCH",
  path: string,
  body?: unknown,
  headers?: Record<string, string>,
): Promise<T> {
  // Auth: session token (OTP) pra rotas do cliente. Pra /admin/*, o token de
  // admin (ADMIN_TOKENS) é injetado pelo fetch-patch global (lib/admin-auth.ts),
  // então aqui não setamos Authorization nessas rotas.
  let authHeader: Record<string, string> = {};
  if (!path.startsWith("/admin")) {
    const token = getSessionToken();
    if (token) authHeader = { Authorization: `Bearer ${token}` };
  }
  const resp = await fetch(`${BASE_URL}${path}`, {
    method: metodo,
    headers: {
      "Content-Type": "application/json",
      Accept: "application/json",
      ...authHeader,
      ...(headers ?? {}),
    },
    body: body ? JSON.stringify(body) : undefined,
  });
  const texto = await resp.text();
  const dados = texto ? safeJson(texto) : undefined;
  if (!resp.ok) {
    const detail =
      (dados as { detail?: string } | undefined)?.detail ?? `${resp.statusText}`;
    throw new ApiError(resp.status, detail, dados);
  }
  return dados as T;
}

function safeJson(txt: string): unknown {
  try {
    return JSON.parse(txt);
  } catch {
    return txt;
  }
}

/** Descrição curta de evento da timeline, derivada do metadata. */
function _resumoEvento(etapa: string, metadata: Record<string, unknown>): string | null {
  const m = metadata ?? {};
  const get = (k: string): unknown => m[k];
  switch (etapa) {
    case "caso_criado": {
      const vertical = get("vertical");
      const forca = get("forca_caso");
      return [vertical, forca ? `força ${forca}` : null].filter(Boolean).join(" · ") || null;
    }
    case "cessao_assinada": {
      const mod = get("modalidade");
      return mod ? `Modalidade ${mod}` : null;
    }
    case "submissao_criada": {
      const canal = get("canal");
      return canal ? `Canal ${canal}` : null;
    }
    case "submissao_aceita": {
      const protocolo = get("protocolo_externo");
      return protocolo ? `Protocolo ${protocolo}` : null;
    }
    case "submissao_encerrada": {
      const estado = get("estado_final");
      return estado ? `Estado final: ${estado}` : null;
    }
    case "encerrado": {
      const sucesso = get("sucesso");
      const valor = get("valor_acordo");
      if (sucesso && typeof valor === "number") {
        return `Acordo de R$ ${(valor / 100).toLocaleString("pt-BR")}`;
      }
      return sucesso ? "Resolvido com sucesso" : "Caso encerrado sem acordo";
    }
    default:
      return null;
  }
}

// ============================================================================
// Tipos compartilhados com o backend (Pydantic models em backend/.../models/caso.py)
// ============================================================================

export type Vertical =
  | "saude"
  | "negativacao"
  | "bancario"
  | "telecom"
  | "energia"
  | "viagens"        // renomeado de "aereo" (ADR 0009)
  | "ecommerce"      // novo Manus (ADR 0008)
  | "inss"           // foco 2026-06-11 — INSS (desconto associativo)
  | "tributario";    // PIVÔ 2026-06-11 — recuperação tributária administrativa

export type ForcaCaso = "fraco" | "medio" | "forte";

export type ModalidadeCessao = "parcial_30" | "integral_desagio";

export type CaminhoModeloHibrido = "A_automatizado" | "A_com_curadoria" | "frustrada_arquivada";

export interface CitacaoVerificada {
  fonte: string;
  referencia: string;
  url: string | null;
}

export interface SolicitacaoTriagem {
  vertical: Vertical;
  detalhes: Record<string, unknown>;
  provas: string[];
  celular?: string | null;
}

export interface RespostaTriagem {
  temCaso: boolean;
  vertical?: Vertical | null;
  valorEstimado?: number | null;
  prazoEstimado?: string | null;
  justificativa?: string | null;
  citacoesVerificadas: CitacaoVerificada[];
  motivo?: string | null;
  casoId?: string | null;
  forcaCaso?: ForcaCaso | null;
  probabilidadeAcordo?: number | null;
  caminhoHibrido?: CaminhoModeloHibrido | null;  // ADR 0009
  requerCuradoriaHumana?: boolean | null;
}

/** Config de fee editável pelo admin (Onda 2 + custódia Onda 4.4). */
export interface ConfigFee {
  feePercentual: number;          // ex.: 0.20 = 20%
  feeMinimoCentavos: number;      // ex.: 10000 = R$ 100,00
  valorMinimoAcordoReais: number; // piso de elegibilidade (reais)
  custodyMode: string;            // 'full' | 'commission_only'
  captadorFeePlataformaPct: number; // modelo captador (fixo): 0.10 = 10%
  captadorFeeCaptadorPct: number;   // modelo captador (fixo): 0.05 = 5%
  fontes: Record<string, string>; // por campo: 'db' | 'env' | 'default'
}

/** Uma faixa da tabela de honorários regressiva (espelho de services.rateio). */
export interface FaixaHonorario {
  indice: number;
  rotulo: string;
  limiteInferior: string;
  limiteSuperior: string | null;
  pctTotal: string;      // "0.20" = 20%
  pctFee: string;        // fee da plataforma
  pctHonorario: string;  // honorário do contador
  customizado: boolean;  // true = override do admin ativo (≠ default do código)
}

/** Tabela de honorários por faixa (config editável pelo admin). */
export interface ConfigFaixas {
  faixas: FaixaHonorario[];
}

/** Cessionária (Art Produções) — vai pro contrato/procuração/formulário-resposta. */
export interface Cessionario {
  razao: string;
  cnpj: string;
  pix: string;
  banco: string;
  endereco: string;
  email: string;
}

/** Ficha de qualificação de um mediador (gestão admin). */
export interface FichaMediador {
  chave?: string;
  preenchido?: boolean;
  completo?: boolean;
  nomeCompleto?: string | null;
  cpf?: string | null;
  curso?: string | null;
  instituicao?: string | null;
  registro?: string | null;
  ativo?: boolean;
}

/** Cliente agregado a partir dos casos (admin). */
export interface ClienteAdmin {
  nome?: string | null;
  cpf?: string | null;
  email?: string | null;
  telefone?: string | null;
  celular_ultimos_4?: string | null;
  pix?: string | null;
  casos: number;
  valor_total_centavos: number;
}

// ============================================================================
// Recuperação tributária (pivô 2026-06-11)
// ============================================================================

export type TeseResultadoDTO = {
  chave: string;
  nome: string;
  credito_estimado: number;
  base: string;
  aplicavel: boolean;
  obs: string;
};

export type RecuperacaoResultadoDTO = {
  teses: TeseResultadoDTO[];
  total_credito: number;
  fee_percentual: number;
  fee_valor: number;
  liquido_cliente: number;
  estimativa: boolean;
};

export type ContratoAssinaturaDTO = {
  ok: boolean;
  documentos: {
    documento: string;
    documentoId: string;
    status: string;
    modo: string;
  }[];
};

/** S5 — resultado da auditoria do motor tributário (M1-M7) de um caso. */
export type AuditoriaCasoResp = {
  casoId: string;
  status: "pendente" | "processando" | "ok" | "erro";
  erro?: string | null;
  atualizadoEm?: string;
  dica?: string;
  payload?: {
    regime: string;
    documentosProcessados: number;
    rbt12Disponivel: boolean;
    totalIndebito: string;
    totalCorrigido: string;
    selicAplicada: boolean;
    pendentesRevisao: number;
    notas: string[];
    memoriaCalculo: Array<{
      competencia: string;
      baseMonofasica: string;
      aliquota: string;
      indebito: string;
      fatorSelic: string | null;
      indebitoCorrigido: string;
      qtdItens: number;
      prescreveEm: string;
      diasRestantes: number;
    }>;
    honorarios: {
      percentual: string;
      honorarios: string;
      liquidoClienteFinal: string;
      base: string;
    };
  } | null;
};

/** Laudo da verificação de poderes do rep. legal + autenticidade do contrato
 *  social (Camadas 1+2). Resultado ADVISÓRIO — o sistema sugere, o contador
 *  confirma; nada é bloqueado automaticamente. */
export type VerificacaoPoderesResp = {
  recomendacao: "ok" | "revisar" | "atencao";
  revisaoRecomendada: boolean;
  camada1: {
    consultou: boolean;
    situacao: string | null;
    situacaoAtiva: boolean | null;
    repLegalNoQsa: boolean | null;
    qualificacaoRepLegal: string | null;
    qsa: Array<{ nome: string; qualificacao: string }>;
    razaoSocialOficial: string | null;
  };
  camada2: {
    executou: boolean;
    temPoderes?: boolean | null;
    formaRepresentacao?: string;
    fundamento?: string;
    confianca?: number;
    observacoes?: string;
    razaoSocialNoContrato?: string;
    administradores?: Array<{ nome: string; podeAssinarSozinho: boolean | null }>;
  };
  flags: Array<{ nivel: "alto" | "medio" | "baixo"; mensagem: string }>;
  fonte: string;
  avisos: string[];
  entrada: { cnpj: string; representanteNome: string; contratoSocialLido: boolean };
};

// ============================================================================
// Endpoints
// ============================================================================

export const api = {
  health: () => request<{ status: string; versao: string }>("GET", "/health"),

  /** PIVÔ 2026-06-11 — calcula a recuperação tributária (todas as teses certas). */
  calcularRecuperacao: (perfil: {
    regime: string;
    faturamento_anual: number;
    folha_mensal: number;
    pct_monofasico: number;
    setor?: string;
    fee_percentual?: number;
  }): Promise<RecuperacaoResultadoDTO> =>
    request<RecuperacaoResultadoDTO>("POST", "/recuperacao/calcular", perfil),

  /** PIVÔ — gera contrato + procuração tributários e envia p/ assinatura (admin). */
  enviarContratoTributario: (
    casoId: string,
    representante?: {
      representante_nome?: string;
      representante_email?: string;
      representante_cpf?: string;
    },
  ): Promise<ContratoAssinaturaDTO> =>
    request<ContratoAssinaturaDTO>(
      "POST",
      `/admin/recuperacao/${casoId}/contrato-assinatura`,
      representante ?? {},
    ),

  // --- Auth ---
  pedirOtp: (celular: string) =>
    request<{ ok: boolean; expira_em: number }>("POST", "/auth/otp", { celular }),

  verificarOtp: async (celular: string, codigo: string) => {
    const r = await request<{ ok: boolean; token_sessao: string | null }>(
      "POST",
      "/auth/verify",
      { celular, codigo },
    );
    // Side-effect: persiste o token de sessão automaticamente quando ok
    // (PR #11 — antes era responsabilidade do caller; agora todo /auth/verify
    // bem-sucedido deixa a sessão pronta pra próximos requests autenticados).
    if (r.ok && r.token_sessao) {
      setSessionToken(r.token_sessao);
    }
    return r;
  },

  // --- Triagem (IA) ---
  triar: (req: SolicitacaoTriagem) =>
    request<RespostaTriagem>("POST", "/triagem", req),

  // --- Casos ---
  criarCaso: (input: {
    celular: string;
    vertical: Vertical;
    detalhes: Record<string, unknown>;
    valorEstimado?: number | null;
    prazoEstimado?: string | null;
    forcaCaso?: ForcaCaso | null;
    pixCliente?: string | null;  // Onda 2.3 — chave PIX do cliente
  }) => request<{ casoId: string; etapa: string; criadoEm: string }>(
    "POST",
    "/casos/criar",
    input,
  ),

  statusCaso: (casoId: string) =>
    request<{
      caso_id: string;
      etapa: string;
      canal: string | null;
      prazo_canal_dias_uteis: number | null;
      atualizado_em: string | null;
    }>("GET", `/casos/${encodeURIComponent(casoId)}/status`),

  timelineCaso: (casoId: string) =>
    request<{
      caso_id: string;
      eventos: Array<{ etapa: string; em: string; metadata: Record<string, unknown> }>;
      total: number;
    }>("GET", `/casos/${encodeURIComponent(casoId)}/timeline`),

  casoPorProtocolo: (protocolo: string) =>
    request<{
      casoId: string;
      vertical: string;
      subtipo: string | null;
      etapa: string;
      operadora: string | null;
      valorEstimado: number | null;
      forcaCaso: string | null;
      canal: string | null;
      prazoCanalDiasUteis: number | null;
      protocoloExterno: string;
      submissaoEstado: string;
      submissaoId: string;
      criadoEm: string;
      atualizadoEm: string | null;
      eventos: Array<{ etapa: string; em: string; metadata: Record<string, unknown> }>;
    }>("GET", `/casos/by-protocolo/${encodeURIComponent(protocolo)}`),

  // --- Cessão ---
  assinarCessao: (input: {
    caso_id: string;
    modalidade?: ModalidadeCessao;
    aceitou_discricionariedade: boolean;
    aceitou_lgpd: boolean;
  }) => request<{
    ok: boolean;
    cessao_id: string;
    assinado_em: string;
    contrato_url: string | null;
  }>("POST", "/cessao/assinar", input),

  // --- Admin ---
  adminStats: () =>
    request<{
      totais: { casos: number; cessoes: number; outcomes: number };
      casos_por_vertical: Record<string, number>;
      casos_por_etapa: Record<string, number>;
      outcomes_por_vertical: Record<string, {
        sucesso: number;
        fracasso: number;
        valor_medio: number | null;
        dias_medio: number | null;
      }>;
    }>("GET", "/admin/stats"),

  // --- B2B (requer Authorization: Bearer ac_...) ---
  b2bOutcomes: (vertical: string, apiKey: string, limite = 100) =>
    request<{ vertical: string; total: number; outcomes: unknown[] }>(
      "GET",
      `/b2b/outcomes/${vertical}?limite=${limite}`,
      undefined,
      { Authorization: `Bearer ${apiKey}` },
    ),

  // ============================================================================
  // ADAPTADORES PARA O FRONTEND MANUS
  // ----------------------------------------------------------------------------
  // O frontend Manus (24 páginas) foi importado na fusão ADR 0008 esperando
  // shape camelCase EN-US do tRPC original. O backend Python usa snake_case
  // PT-BR. Estes adaptadores fazem a ponte; quando o endpoint backend não
  // existe ainda, retornam mock estável pra não quebrar a UI.
  // ============================================================================

  /** Cria um caso a partir do shape Manus (CaseFlow.tsx). */
  criarCasoManus: async (input: {
    clientName: string;
    clientCpf: string;
    clientEmail: string;
    clientPhone: string;
    sector: Vertical;
    decisionAnswers: Record<string, string>;
    problemDescription?: string;
    amountInvolved?: number | string;
    companyName?: string;
    clientPix?: string;  // Onda 2.3 — chave PIX do cliente (repasse)
  }): Promise<{ caseId: string; etapa: string; criadoEm: string }> => {
    const valorEstimado = typeof input.amountInvolved === "string"
      ? parseFloat(input.amountInvolved.replace(/[^\d.]/g, "")) || null
      : input.amountInvolved ?? null;

    try {
      const r = await api.criarCaso({
        celular: input.clientPhone,
        vertical: input.sector,
        detalhes: {
          ...input.decisionAnswers,
          nome: input.clientName,
          cpf: input.clientCpf,
          email: input.clientEmail,
          descricao: input.problemDescription,
          empresa: input.companyName,
        },
        valorEstimado,
        pixCliente: input.clientPix ?? null,
      });
      return { caseId: r.casoId, etapa: r.etapa, criadoEm: r.criadoEm };
    } catch (e) {
      // Fallback mock: gera caseId sintético se backend não disponível
      const caseId = `mock_${Date.now().toString(36)}`;
      return { caseId, etapa: "aguardando_cessao", criadoEm: new Date().toISOString() };
    }
  },

  /** Upload de documento via base64 (PR #9). */
  uploadDocumento: async (input: {
    caseId: string;
    fileName: string;
    mimeType: string;
    fileBase64: string;
    documentType?: string;
    description?: string;
  }): Promise<{ ok: true; documentId: string; bytes: number; sha256: string }> => {
    try {
      const r = await request<{
        ok: boolean;
        documentoId: string;
        nomeOriginal: string;
        bytes: number;
        sha256: string;
      }>("POST", `/casos/${encodeURIComponent(input.caseId)}/documentos`, {
        nomeOriginal: input.fileName,
        mimeType: input.mimeType,
        conteudoBase64: input.fileBase64,
        tipoDocumento: input.documentType ?? null,
        descricao: input.description ?? null,
      });
      return {
        ok: true,
        documentId: r.documentoId,
        bytes: r.bytes,
        sha256: r.sha256,
      };
    } catch (e) {
      // Fallback mock (backend offline / sem sessão em dev)
      void e;
      return {
        ok: true,
        documentId: `doc_${Date.now().toString(36)}`,
        bytes: 0,
        sha256: "",
      };
    }
  },

  /** Lista documentos anexados ao caso (PR #9). */
  listarDocumentos: async (
    caseId: string,
  ): Promise<Array<{
    documentId: string;
    fileName: string;
    mimeType: string;
    documentType: string | null;
    description: string | null;
    bytes: number | null;
    sha256: string | null;
    createdAt: string;
  }>> => {
    try {
      const r = await request<{
        casoId: string;
        total: number;
        documentos: Array<{
          documentoId: string;
          nomeOriginal: string;
          mimeType: string;
          tipoDocumento: string | null;
          descricao: string | null;
          bytes: number | null;
          sha256: string | null;
          criadoEm: string;
        }>;
      }>("GET", `/casos/${encodeURIComponent(caseId)}/documentos`);
      return r.documentos.map((d) => ({
        documentId: d.documentoId,
        fileName: d.nomeOriginal,
        mimeType: d.mimeType,
        documentType: d.tipoDocumento,
        description: d.descricao,
        bytes: d.bytes,
        sha256: d.sha256,
        createdAt: d.criadoEm,
      }));
    } catch {
      return [];
    }
  },

  /** Remove um documento (PR #11). */
  deletarDocumento: async (
    caseId: string,
    documentId: string,
  ): Promise<{ ok: boolean }> => {
    try {
      const r = await request<{ ok: boolean }>(
        "DELETE",
        `/casos/${encodeURIComponent(caseId)}/documentos/${encodeURIComponent(
          documentId,
        )}`,
      );
      return r;
    } catch {
      return { ok: false };
    }
  },

  /** Info do usuário logado (PR #9 — GET /auth/me). */
  authMe: async (): Promise<{
    id: number;
    name: string;
    email: string;
    role: "user" | "admin";
  } | null> => {
    // Sem token persistido = sem sessão; retorna null sem chamar backend
    if (!getSessionToken()) {
      // Mock dev sintético para a UI renderizar enquanto OTP não rola
      try {
        const cached = localStorage.getItem("acordei-user-info");
        if (cached && cached !== "null") return JSON.parse(cached);
      } catch { /* noop */ }
      return { id: 1, name: "Visitante Dev", email: "dev@acordei.app.br", role: "user" };
    }
    try {
      const r = await request<{
        autenticado: boolean;
        celular_ultimos_4: string;
        sessao_criada_em: string;
        sessao_expira_em: string;
      }>("GET", "/auth/me");
      // Schema do backend ainda não retorna name/email; deriva celular pro display
      return {
        id: 1,
        name: `Cliente ****${r.celular_ultimos_4}`,
        email: "—",
        role: "user",
      };
    } catch (e) {
      // 401 → sessão expirou; limpa token persistido
      if (e instanceof ApiError && e.status === 401) {
        setSessionToken(null);
      }
      return null;
    }
  },

  /** Exporta todos os dados pessoais do usuário (LGPD art. 18 II/V — PR #12). */
  exportarMeusDados: async (): Promise<{
    geradoEm: string;
    celularUltimos4: string;
    sessaoAtual: Record<string, string>;
    casos: Array<Record<string, unknown>>;
    documentos: Array<Record<string, unknown>>;
    nota: string;
  } | null> => {
    try {
      return await request("GET", "/auth/export-data");
    } catch {
      return null;
    }
  },

  /** Apaga conta do usuário (LGPD art. 18 IV — PR #12). Irreversível. */
  deletarMinhaConta: async (): Promise<{
    ok: boolean;
    sessoesRemovidas: number;
    documentosRemovidos: number;
    casosMarcadosExcluidos: number;
    nota?: string;
  }> => {
    try {
      const r = await request<{
        ok: boolean;
        sessoesRemovidas: number;
        documentosRemovidos: number;
        casosMarcadosExcluidos: number;
        nota?: string;
      }>("DELETE", "/auth/account");
      // Conta deletada → limpa token persistido localmente
      setSessionToken(null);
      try { localStorage.removeItem("acordei-user-info"); } catch { /* noop */ }
      return r;
    } catch {
      return {
        ok: false,
        sessoesRemovidas: 0,
        documentosRemovidos: 0,
        casosMarcadosExcluidos: 0,
      };
    }
  },

  /** Logout (PR #9 — POST /auth/logout). */
  authLogout: async (): Promise<{ ok: true }> => {
    try {
      await request<{ ok: boolean }>("POST", "/auth/logout");
    } catch { /* idempotente backend; ignora erro de rede */ }
    setSessionToken(null);
    try { localStorage.removeItem("acordei-user-info"); } catch { /* noop */ }
    return { ok: true };
  },

  /** Lista os casos do usuário logado (PR #9 — GET /casos/meus). */
  casosMeus: async (
    _celular?: string,
  ): Promise<Array<{
    caseId: string;
    sector: string;
    status: string;
    createdAt: string;
    companyName: string | null;
    amountInvolved: number | null;
    eligibility: "mediation" | "out_of_scope";
  }>> => {
    void _celular;
    try {
      const r = await request<{
        total: number;
        casos: Array<{
          casoId: string;
          vertical: string;
          subtipo: string | null;
          etapa: string;
          valorEstimado: number | null;
          forcaCaso: string | null;
          criadoEm: string;
          atualizadoEm: string;
        }>;
      }>("GET", "/casos/meus");
      return r.casos.map((c) => ({
        caseId: c.casoId,
        sector: c.vertical,
        status: c.etapa,
        createdAt: c.criadoEm,
        companyName: null,
        amountInvolved: c.valorEstimado,
        eligibility: "mediation" as const,
      }));
    } catch {
      return []; // backend offline / sem sessão → vazio
    }
  },

  /** Detalhe completo de um caso (compõe status + timeline + campos LLM placeholder). */
  casoDetalhe: async (caseId: string): Promise<{
    caseId: string;
    etapa: string;
    canal: string | null;
    prazoCanalDiasUteis: number | null;
    atualizadoEm: string | null;
    timeline: Array<{ etapa: string; em: string; metadata: Record<string, unknown> }>;
    // --- Campos esperados pelas páginas Manus (mock até backend implementar) ---
    sector: string;
    eligibility: "mediation" | "out_of_scope";
    requiresHumanIntervention: "yes" | "no";
    companyName: string | null;
    amountInvolved: number | null;
    llmScore: number | null;
    llmProposal: string | null;
    llmRiskLevel: string | null;
    llmRecommendedAction: string | null;
  } | null> => {
    const base = {
      caseId,
      sector: "ecommerce" as string,
      eligibility: "mediation" as const,
      requiresHumanIntervention: "no" as const,
      companyName: null as string | null,
      amountInvolved: null as number | null,
      llmScore: null as number | null,
      llmProposal: null as string | null,
      llmRiskLevel: null as string | null,
      llmRecommendedAction: null as string | null,
    };
    try {
      const [status, timeline] = await Promise.all([
        api.statusCaso(caseId),
        api.timelineCaso(caseId).catch(() => ({ caso_id: caseId, eventos: [], total: 0 })),
      ]);
      return {
        ...base,
        etapa: status.etapa,
        canal: status.canal,
        prazoCanalDiasUteis: status.prazo_canal_dias_uteis,
        atualizadoEm: status.atualizado_em,
        timeline: timeline.eventos,
      };
    } catch {
      // Backend offline ou caso não encontrado → mock estável
      return {
        ...base,
        etapa: "aguardando_resposta_canal_regulatorio",
        canal: "consumidor.gov.br",
        prazoCanalDiasUteis: 10,
        atualizadoEm: new Date().toISOString(),
        timeline: [],
      };
    }
  },

  /** Busca caso pelo protocolo externo + adapta pro shape Manus (Acompanhar.tsx). */
  casoPorProtocoloManus: async (protocolo: string): Promise<{
    id: string;
    status: string;
    sector: string;
    companyName: string | null;
    createdAt: string;
    llmScore: number | null;
    llmRiskLevel: string | null;
    llmRecommendedAction: string | null;
    externalProtocol: string;
    timeline: Array<{
      id: string;
      eventType: string;
      title: string;
      description: string | null;
      createdAt: string;
    }>;
  } | null> => {
    try {
      const caso = await api.casoPorProtocolo(protocolo);

      // Map etapa do backend → status do front (shape Manus)
      const STATUS_MAP: Record<string, string> = {
        aguardando_cessao: "draft",
        cessao_assinada: "under_review",
        aguardando_resposta_canal_regulatorio: "negotiating",
        encerrado_com_sucesso: "resolved",
        frustrada_arquivada: "closed",
        // Bloco U — fluxo tributário RFB
        aguardando_submissao: "under_review",
        submetido_rfb: "negotiating",
        credito_constituido: "resolved",
      };
      const status = STATUS_MAP[caso.etapa] ?? "submitted";

      // Mapeamento força → score sintético (0-100)
      const SCORE_MAP: Record<string, number> = {
        forte: 82,
        medio: 58,
        fraco: 32,
      };
      const RISK_MAP: Record<string, string> = {
        forte: "low",
        medio: "medium",
        fraco: "high",
      };
      const ACTION_MAP: Record<string, string> = {
        forte: "Mediação tem alta chance de êxito",
        medio: "Mediação possível com curadoria",
        fraco: "Caso fora do escopo de mediação automática",
      };

      // Map eventos da timeline → shape Manus
      const EVENT_TITLE: Record<string, string> = {
        caso_criado: "Caso criado",
        cessao_assinada: "Cessão de crédito assinada",
        submissao_criada: "Submissão preparada",
        submissao_aceita: "Protocolo registrado no canal regulatório",
        submissao_encerrada: "Submissão encerrada",
        encerrado: "Caso encerrado",
        // Bloco U — fluxo tributário RFB
        aguardando_submissao: "Auditoria e contrato concluídos — pronto para a RFB",
        submetido_rfb: "Pedido transmitido à Receita Federal",
        credito_constituido: "Crédito homologado pela Receita Federal",
      };
      const EVENT_TYPE_MAP: Record<string, string> = {
        caso_criado: "case_created",
        cessao_assinada: "documents_uploaded",
        submissao_criada: "llm_analyzed",
        submissao_aceita: "submitted_to_portal",
        submissao_encerrada: "company_responded",
        encerrado: "case_resolved",
      };

      return {
        id: caso.casoId,
        status,
        sector: caso.vertical,
        companyName: caso.operadora,
        createdAt: caso.criadoEm,
        llmScore: caso.forcaCaso ? SCORE_MAP[caso.forcaCaso] ?? null : null,
        llmRiskLevel: caso.forcaCaso ? RISK_MAP[caso.forcaCaso] ?? null : null,
        llmRecommendedAction: caso.forcaCaso ? ACTION_MAP[caso.forcaCaso] ?? null : null,
        externalProtocol: caso.protocoloExterno,
        timeline: caso.eventos.map((e, idx) => ({
          id: `${caso.casoId}_${idx}`,
          eventType: EVENT_TYPE_MAP[e.etapa] ?? "default",
          title: EVENT_TITLE[e.etapa] ?? e.etapa,
          description: _resumoEvento(e.etapa, e.metadata),
          createdAt: e.em,
        })),
      };
    } catch (err) {
      if (err instanceof ApiError && err.status === 404) return null;
      throw err;
    }
  },

  // ─── Notificações in-app (PR #22) ───────────────────────────────────────

  /** Lista notificações do usuário logado. Filtros opcionais (PR #30):
   *  `apenasNaoLidas`, `casoId` (defesa IDOR aplicada). */
  notificacoesMeus: (opts?: {
    apenasNaoLidas?: boolean;
    casoId?: string;
    limite?: number;
  }) => {
    const qs = new URLSearchParams();
    if (opts?.apenasNaoLidas) qs.set("apenasNaoLidas", "true");
    if (opts?.casoId) qs.set("casoId", opts.casoId);
    qs.set("limite", String(opts?.limite ?? 50));
    return request<{
      total: number;
      naoLidas: number;
      notificacoes: Array<{
        id: string;
        casoId: string;
        tipo: string;
        titulo: string;
        descricao: string | null;
        payload: Record<string, unknown>;
        lida: boolean;
        criadoEm: string;
        lidaEm: string | null;
      }>;
    }>("GET", `/notificacoes/meus?${qs.toString()}`);
  },

  /** Contagem de não lidas — usado no badge da NotificationBell. */
  notificacoesContagem: () =>
    request<{ naoLidas: number }>("GET", "/notificacoes/contagem"),

  /** Marca UMA notificação como lida. */
  notificacaoMarcarLida: (id: string) =>
    request<{ ok: boolean }>(
      "POST",
      `/notificacoes/${encodeURIComponent(id)}/marcar-lida`,
    ),

  /** Marca todas como lidas (botão 'Marcar tudo'). */
  notificacoesMarcarTodasLidas: () =>
    request<{ ok: boolean; marcadas: number }>(
      "POST",
      "/notificacoes/marcar-todas-lidas",
    ),

  // ─── Propostas (PR #14) ─────────────────────────────────────────────────

  /** Gera (ou regenera) a proposta de acordo do caso. Idempotente: F5
   *  retorna a mesma proposta pending sem duplicar. */
  gerarProposta: (caseId: string) =>
    request<{
      propostaId: string;
      casoId: string;
      status: "pending" | "accepted" | "rejected";
      valorSugerido: number | null;
      resumo: string;
      fundamentacaoLegal: string;
      argumentos: string;
      contraEstrategias: string;
      proximosPassos: string[];
      counterText: string | null;
      contratoUrl: string | null;
      geradaEm: string;
      aceitaEm: string | null;
      rejeitadaEm: string | null;
    }>("POST", `/casos/${encodeURIComponent(caseId)}/proposta/gerar`),

  /** Retorna a proposta corrente do caso (qualquer status). 404 se nunca gerada. */
  obterProposta: (caseId: string) =>
    request<{
      propostaId: string;
      casoId: string;
      status: "pending" | "accepted" | "rejected";
      valorSugerido: number | null;
      resumo: string;
      fundamentacaoLegal: string;
      argumentos: string;
      contraEstrategias: string;
      proximosPassos: string[];
      counterText: string | null;
      contratoUrl: string | null;
      geradaEm: string;
      aceitaEm: string | null;
      rejeitadaEm: string | null;
    }>("GET", `/casos/${encodeURIComponent(caseId)}/proposta`),

  /** Marca proposta pending como accepted; backend gera contratoUrl placeholder. */
  aceitarProposta: (caseId: string) =>
    request<{
      propostaId: string;
      status: "accepted";
      contratoUrl: string | null;
    }>("POST", `/casos/${encodeURIComponent(caseId)}/proposta/aceitar`),

  /** Marca proposta pending como rejected (com counterText opcional). */
  rejeitarProposta: (caseId: string, counterText?: string | null) =>
    request<{
      propostaId: string;
      status: "rejected";
      counterText: string | null;
    }>(
      "POST",
      `/casos/${encodeURIComponent(caseId)}/proposta/rejeitar`,
      { counterText: counterText ?? null },
    ),

  /** Submete o caso ao canal regulatório (POST /casos/{id}/submeter — ADR 0010). */
  submeterCaso: async (caseId: string): Promise<{
    simulado: boolean;
    protocol: string | null;
    estado: string;
  }> => {
    try {
      const r = await request<{
        submissao_id: string;
        estado: string;
        protocolo_externo: string | null;
        canal: string;
        mock: boolean;
      }>("POST", `/casos/${encodeURIComponent(caseId)}/submeter`);
      return {
        simulado: r.mock,
        protocol: r.protocolo_externo,
        estado: r.estado,
      };
    } catch (e) {
      // Re-lança pra mutation tratar (mostra mensagem de erro no UI)
      throw e;
    }
  },

  // ─── Admin actions (PR #15) ─────────────────────────────────────────────

  /** Detalhe rico de um caso (admin — GET /admin/casos/{id}). PR #15. */
  adminCasoDetalhe: async (caseId: string): Promise<{
    casoId: string;
    celularUltimos4: string;
    vertical: string;
    subtipo: string | null;
    etapa: string;
    detalhes: Record<string, unknown>;
    valorEstimado: number | null;
    forcaCaso: string | null;
    prazoEstimado: string | null;
    criadoEm: string;
    atualizadoEm: string;
    submissoes: Array<Record<string, unknown>>;
    documentos: Array<Record<string, unknown>>;
    propostaAtual: Record<string, unknown> | null;
  } | null> => {
    try {
      return await request("GET", `/admin/casos/${encodeURIComponent(caseId)}`);
    } catch {
      return null;
    }
  },

  /** S5 — auditoria do motor tributário (visão do CLIENTE, sessão própria). */
  auditoriaCaso: (casoId: string) =>
    request<AuditoriaCasoResp>(
      "GET", `/casos/${encodeURIComponent(casoId)}/auditoria`),

  /** S5 — auditoria do motor (mediador/admin): consulta e disparo. */
  adminAuditoriaCaso: (casoId: string) =>
    request<AuditoriaCasoResp>(
      "GET", `/admin/recuperacao/${encodeURIComponent(casoId)}/auditoria`),
  adminRodarAuditoria: (casoId: string) =>
    request<{ ok: boolean; status: string }>(
      "POST", `/admin/recuperacao/${encodeURIComponent(casoId)}/auditoria/rodar`),

  /** Bloco U — os 2 gates do contador no fluxo tributário. */
  adminFluxoRfb: (casoId: string) =>
    request<{
      casoId: string;
      etapa: string;
      eventos: Array<{
        evento: "submissao" | "constituicao";
        payload: Record<string, string>;
        criadoPor: string;
        criadoEm: string;
      }>;
    }>("GET", `/admin/recuperacao/${encodeURIComponent(casoId)}/fluxo`),
  adminGateSubmissao: (casoId: string, protocolo: string, observacoes = "") =>
    request<{ ok: boolean; etapa: string }>(
      "POST", `/admin/recuperacao/${encodeURIComponent(casoId)}/gate/submissao`,
      { protocolo, observacoes }),
  adminGateConstituicao: (casoId: string, valorHomologado: number, observacoes = "") =>
    request<{ ok: boolean; etapa: string; honorarios: string; liquidoCliente: string }>(
      "POST", `/admin/recuperacao/${encodeURIComponent(casoId)}/gate/constituicao`,
      { valor_homologado: valorHomologado, observacoes }),
  /** Roteiro guiado de submissão no e-CAC (assistente do Gate 1). Fonte única
   *  services.ecac_guia — os MESMOS passos do Relatório de Auditoria (PDF). */
  adminEcacGuia: (casoId: string) =>
    request<{
      casoId: string;
      etapa: string;
      auditoriaConcluida: boolean;
      modelo: string;
      quemSubmete: string;
      regime: string;
      competencias: string[];
      caderno: Array<{
        competencia: string;
        baseMonofasica: string;
        indebito: string;
        indebitoCorrigido: string;
        diasRestantes: number | null;
      }>;
      totalCorrigido: string | null;
      portalEcac: string;
      passos: Array<{
        ordem: number;
        servico: string;
        acao: string;
        titulo: string;
        descricao: string;
        caminho: string;
        url: string | null;
        fundamento: string;
        opcional: boolean;
      }>;
      avisos: string[];
    }>("GET", `/admin/recuperacao/${encodeURIComponent(casoId)}/ecac-guia`),

  /** Consulta cadastral de CNPJ (API pública gratuita) — onboarding. ADR 0012 §7.3. */
  adminConsultarCnpj: (cnpj: string) =>
    request<{
      ok: boolean;
      cnpj: string;
      razaoSocial: string;
      nomeFantasia: string;
      situacao: string;
      dataAbertura: string;
      cnae: string;
      cnaeDescricao: string;
      porte: string;
      naturezaJuridica: string;
      uf: string;
      municipio: string;
      qsa: Array<{ nome: string; qualificacao: string }>;
    }>("GET", `/admin/recuperacao/cnpj/${encodeURIComponent(cnpj)}`),

  /** Verificação de poderes do rep. legal + autenticidade do contrato social.
   *  GET = Camada 1 (Consulta CNPJ/QSA, custo zero). POST = +Camada 2 (a IA lê o
   *  contrato social anexado). Resultado ADVISÓRIO — o contador confirma. */
  adminVerificacaoPoderes: (casoId: string) =>
    request<VerificacaoPoderesResp>(
      "GET", `/admin/recuperacao/${encodeURIComponent(casoId)}/verificacao-poderes`),
  adminVerificacaoPoderesCompleta: (
    casoId: string,
    body: {
      analisar_contrato_social?: boolean;
      contrato_social_texto?: string;
      representante_nome?: string;
      cnpj?: string;
    } = {},
  ) =>
    request<VerificacaoPoderesResp>(
      "POST", `/admin/recuperacao/${encodeURIComponent(casoId)}/verificacao-poderes`,
      body),

  /** Histórico COMPLETO do caso (Onda 3 — ficha rica do mediador).
   *  Junta caso + timeline + submissões + documentos + propostas + pix +
   *  notificações + outcome em 1 chamada. Campos do caso vêm snake_case. */
  adminHistoricoCompleto: (casoId: string) =>
    request<{
      caso: {
        id: string;
        celular_ultimos_4: string;
        vertical: string;
        subtipo: string | null;
        etapa: string;
        valor_estimado: number | null;
        prazo_estimado: string | null;
        forca_caso: string | null;
        detalhes: Record<string, unknown>;
        fee_percentual_assinado: number | null;
        pix_cliente: string | null;
        fee_status: string | null;
        fee_faturado_em: string | null;
        fee_pago_em: string | null;
        criado_em: string;
        atualizado_em: string;
      };
      timeline: Array<{ etapa: string; em: string; metadata: Record<string, unknown> }>;
      submissoes: Array<Record<string, unknown>>;
      documentos: Array<Record<string, unknown>>;
      propostas: Array<Record<string, unknown>>;
      pixPagamentos: Array<Record<string, unknown>>;
      notificacoes: Array<Record<string, unknown>>;
      outcome: Record<string, unknown> | null;
      pecaDefesa: {
        id: string;
        texto: string;
        orgao_alvo: string | null;
        fonte: string;
        citacoes: string[];
        gerada_em: string;
      } | null;
    }>("GET", `/admin/casos/${encodeURIComponent(casoId)}/historico-completo`),

  /** Billing baseline A — admin marca o status de cobrança do fee da plataforma. */
  adminSetFeeStatus: (casoId: string, status: string, observacao?: string) =>
    request<{ ok: boolean; caso_id: string; fee_status: string; marcos: Record<string, string> }>(
      "POST",
      `/admin/casos/${encodeURIComponent(casoId)}/fee-status`,
      { status, observacao: observacao ?? null },
    ),

  /** Curador muda a etapa do caso manualmente (PR #15). */
  adminAtualizarStatus: (caseId: string, novaEtapa: string, observacao?: string) =>
    request<{ ok: boolean; novaEtapa: string }>(
      "POST",
      `/admin/casos/${encodeURIComponent(caseId)}/status`,
      { novaEtapa, observacao: observacao ?? null },
    ),

  /** Curador pede docs adicionais ao cliente (PR #15). */
  adminSolicitarEvidencia: (caseId: string, documentosSolicitados: string) =>
    request<{ ok: boolean; casoId: string; novaEtapa: string }>(
      "POST",
      `/admin/casos/${encodeURIComponent(caseId)}/solicitar-evidencia`,
      { documentosSolicitados },
    ),

  /** SLA stats histórico — taxa de cumprimento + mediana + p95 (PR #76). */
  adminSlaStats: (dias: number = 30) =>
    request<{
      geradoEm: string;
      janelaDias: number;
      totalResolvidos: number;
      cumprimentoPct: number | null;
      medianaDias: number | null;
      p95Dias: number | null;
      breaches: number;
      porVertical: Array<{
        vertical: string;
        slaConfiguradoDias: number;
        totalResolvidos: number;
        dentroSla: number;
        breaches: number;
        cumprimentoPct: number;
        medianaDias: number | null;
      }>;
    }>("GET", `/admin/dashboard/sla-stats?dias=${dias}`),

  /** Pulso de produtividade do mediador (PR #62). */
  adminPulsoMediador: () =>
    request<{
      geradoEm: string;
      acoes24h: Array<{ acao: string; acaoLabel: string; total: number }>;
      acoes7d: Array<{ acao: string; acaoLabel: string; total: number }>;
      casosProcessadosHoje: { resolvidos: number; frustrados: number; total: number };
      filaAtual: Record<string, number>;
      filaTotal: number;
      serieDiaria: Array<{ dia: string; total: number }>;
      totalAcoes24h: number;
      totalAcoes7d: number;
    }>("GET", "/admin/dashboard/pulso-mediador"),

  /** Aplica ação em N casos da curadoria (PR #59). */
  adminCuradoriaAcaoEmLote: (input: {
    casoIds: string[];
    acao: "marcar_evidencia_ok" | "escalar" | "arquivar";
    observacao?: string;
  }) =>
    request<{
      acao: string;
      novaEtapa: string;
      totalSolicitados: number;
      sucesso: string[];
      falha: Array<{ caso_id: string; motivo: string }>;
      totalSucesso: number;
      totalFalha: number;
    }>("POST", "/admin/curadoria/acao-em-lote", input),

  /** Audit log: últimas ações admin/curador (PR #60). */
  adminAuditLog: (opts?: { limite?: number; acao?: string; casoId?: string; desdeHoras?: number }) => {
    const qs = new URLSearchParams();
    qs.set("limite", String(opts?.limite ?? 50));
    if (opts?.acao) qs.set("acao", opts.acao);
    if (opts?.casoId) qs.set("caso_id", opts.casoId);
    qs.set("desde_horas", String(opts?.desdeHoras ?? 168));
    return request<{
      total: number;
      janelaHoras: number;
      acoes: Array<{
        id: string;
        ator: string;
        acao: string;
        acaoLabel: string;
        alvoCasoId: string | null;
        payload: Record<string, unknown> | null;
        criadoEm: string;
      }>;
    }>("GET", `/admin/audit-log?${qs.toString()}`);
  },

  /** Contagem de ações por tipo (PR #60). */
  adminAuditLogAgregadas: (janelaHoras = 24) =>
    request<{
      janelaHoras: number;
      agregadas: Array<{
        acao: string;
        acaoLabel: string;
        total: number;
        ultima: string;
      }>;
    }>("GET", `/admin/audit-log/agregadas?janela_horas=${janelaHoras}`),

  /** Ping de uptime de URL externa (PR #58). Default: prod acordei.app.br. */

  /** Ping de uptime de URL externa (PR #58). Default: prod acordei.app.br. */
  adminUptimeCheck: (url?: string) => {
    const qs = url ? `?url=${encodeURIComponent(url)}` : "";
    return request<{
      url: string;
      online: boolean;
      status_code: number | null;
      duracao_ms: number;
      body_preview: string | null;
      erro: string | null;
      verificado_em: string;
    }>("GET", `/admin/dashboard/uptime-check${qs}`);
  },

  /** Pulso operacional do sistema (PR #56). Custo Claude + scheduler + DB + integrações. */
  adminDashboardOperacional: () =>
    request<{
      geradoEm: string;
      claude: {
        custo_total_usd: number;
        custo_24h_usd: number;
        calls_total: number;
        calls_ok: number;
        calls_falha: number;
        calls_24h: number;
        tokens_input_total: number;
        tokens_output_total: number;
      };
      casos24h: { criados: number; resolvidos: number; frustrados: number };
      db: { total_casos: number; size_mb: number | null; path: string };
      errosJs24h: number;
      ultimaAtividade: string | null;
      scheduler: {
        running: boolean;
        habilitado: boolean;
        intervalo_auto_resolver_s: number;
        cleanup_habilitado: boolean;
        cleanup_intervalo_h: number | null;
        jobs: Array<{
          id: string;
          name: string;
          next_run: string | null;
          trigger: string;
          pending: boolean;
        }>;
      };
      integracoes: {
        claude: { configurado: boolean; modo: string };
        asaas: { configurado: boolean; modo: string };
        smtp: { configurado: boolean; modo: string };
      };
    }>("GET", `/admin/dashboard/operacional`),

  /** Funnel de conversão por estágio (PR #50, comparativo no PR #70). */
  adminDashboardFunil: (dias: number = 30, compare: boolean = false) =>
    request<{
      geradoEm: string;
      estagios: Array<{
        nome: string;
        count: number;
        etapasIncluidas: string[];
        conversaoParaProximoPct: number | null;
        countPeriodoAnterior?: number;
        deltaPct?: number | null;
      }>;
      frustrados: number;
      totalCasosJanela: number;
      taxaConversaoTotalPct: number;
      janelaDias: number;
      comparativo: {
        totalCasosAnterior: number;
        deltaTotalPct: number | null;
        taxaConversaoAnteriorPct: number;
        deltaConversaoPct: number;
        frustradosAnterior: number;
      } | null;
    }>("GET", `/admin/dashboard/funil?dias=${dias}&compare=${compare}`),

  /** Dashboard de cohort retention (PR #41, comparativo PR #73). */
  adminDashboardCohort: (semanas: number = 12, compare: boolean = false) =>
    request<{
      geradoEm: string;
      semanas: Array<{
        semana: string;
        casosCriados: number;
        casosResolvidos: number;
        casosFrustrados: number;
        casosPendentes: number;
        taxaResolucaoPct: number;
        ticketMedioCentavos: number | null;
        diasMedioResolucao: number | null;
      }>;
      porVertical: Array<{
        vertical: string;
        totalCasos: number;
        resolvidos: number;
        frustrados: number;
        taxaResolucaoPct: number;
        ticketMedioCentavos: number | null;
        diasMedioResolucao: number | null;
      }>;
      totais: Record<string, number>;
      comparativo: {
        totaisAnterior: Record<string, number>;
        deltaCriadosPct: number | null;
        deltaResolvidosPct: number | null;
        deltaTaxaResolucaoPp: number;
      } | null;
    }>("GET", `/admin/dashboard/cohort?semanas=${semanas}&compare=${compare}`),

  /** Encerra caso manualmente (PR #37). Sucesso=True exige valorAcordoCentavos.
   *  Cria outcome (moat ML) e dispara notificação `case_resolved` ou
   *  `case_escalated`. */
  adminEncerrarManual: (
    caseId: string,
    input: {
      sucesso: boolean;
      motivo: string;
      valorAcordoCentavos?: number;
      diasResolucao?: number;
    },
  ) =>
    request<{
      ok: boolean;
      casoId: string;
      novaEtapa: string;
      outcomeCriado: boolean;
    }>("POST", `/admin/casos/${encodeURIComponent(caseId)}/encerrar-manual`, input),

  /** Cria cobrança Pix pra resolver o caso (PR #21). Webhook Asaas
   *  marca caso resolvido quando PAYMENT_RECEIVED entrar. */
  adminResolveWithPix: (
    caseId: string,
    input: {
      valorCentavos: number;
      descricao: string;
      cpfPagador?: string;
      nomePagador?: string;
      emailPagador?: string;
      expiracaoSegundos?: number;
    },
  ) =>
    request<{
      ok: boolean;
      pixId: string;
      externalId: string;
      valorCentavos: number;
      status: string;
      txid: string | null;
      qrCode: string | null;
      qrCodeImagem: string | null;
      expiraEm: string | null;
      chavePix: string | null;
      novaEtapa: string;
      modo: "real" | "mock";
    }>("POST", `/admin/casos/${encodeURIComponent(caseId)}/resolve-with-pix`, input),

  /** Envia e-mail transacional ao cliente do caso (PR #20). */
  adminSendEmail: (
    caseId: string,
    input: { assunto: string; mensagem: string; destinatarioEmail?: string },
  ) =>
    request<{
      ok: boolean;
      enviado: boolean;
      messageId: string;
      destinatarioDominio: string;
      modo: "real" | "mock";
    }>("POST", `/admin/casos/${encodeURIComponent(caseId)}/send-email`, input),

  /** Roda análise LLM do caso sem persistir proposta (PR #19). */
  adminAnalyze: (caseId: string) =>
    request<{
      casoId: string;
      resumo: string;
      fundamentacaoLegal: string;
      argumentos: string;
      contraEstrategias: string;
      proximosPassos: string[];
      valorSugerido: number | null;
      modeloUsado: string | null;
      regenerou: boolean;
      citacoesVerificadas: number;
    }>("POST", `/admin/casos/${encodeURIComponent(caseId)}/analyze`),

  /** Fila de curadoria humana com SLA breach detection (PR #18) + filtros
   * avançados PR #59 (vertical, força, valor range, data range, busca por protocolo). */
  adminCuradoria: (opts?: {
    prioridade?: "alta" | "media" | "baixa";
    etapa?: string;
    vertical?: string;
    forca?: "fraco" | "medio" | "forte";
    valorMin?: number;
    valorMax?: number;
    dataDe?: string;  // ISO YYYY-MM-DD
    dataAte?: string;
    q?: string;
    pagina?: number;
    porPagina?: number;
  }) => {
    const qs = new URLSearchParams();
    if (opts?.prioridade) qs.set("prioridade", opts.prioridade);
    if (opts?.etapa) qs.set("etapa", opts.etapa);
    if (opts?.vertical) qs.set("vertical", opts.vertical);
    if (opts?.forca) qs.set("forca", opts.forca);
    if (opts?.valorMin != null) qs.set("valor_min", String(opts.valorMin));
    if (opts?.valorMax != null) qs.set("valor_max", String(opts.valorMax));
    if (opts?.dataDe) qs.set("data_de", opts.dataDe);
    if (opts?.dataAte) qs.set("data_ate", opts.dataAte);
    if (opts?.q) qs.set("q", opts.q);
    qs.set("pagina", String(opts?.pagina ?? 1));
    qs.set("por_pagina", String(opts?.porPagina ?? 50));
    return request<{
      total: number;
      pagina: number;
      porPagina: number;
      breakdownPorEtapa: Record<string, number>;
      casos: Array<{
        casoId: string;
        celularUltimos4: string;
        vertical: string;
        subtipo: string | null;
        etapa: string;
        valorEstimado: number | null;
        forcaCaso: string | null;
        criadoEm: string;
        atualizadoEm: string;
        diasParado: number;
        slaDias: number;
        slaBreach: boolean;
        prioridade: "alta" | "media" | "baixa";
      }>;
    }>("GET", `/admin/curadoria?${qs.toString()}`);
  },

  /** Lista erros JS recentes capturados pelo ErrorBoundary/window/promise (PR #43→#46). */
  adminClientErrors: (opts?: {
    limite?: number;
    origem?: "boundary" | "window" | "promise" | "manual";
    desdeHoras?: number;
  }) => {
    const qs = new URLSearchParams();
    qs.set("limite", String(opts?.limite ?? 100));
    if (opts?.origem) qs.set("origem", opts.origem);
    qs.set("desde_horas", String(opts?.desdeHoras ?? 72));
    return request<{
      total: number;
      janelaHoras: number;
      erros: Array<{
        id: string;
        origem: "boundary" | "window" | "promise" | "manual";
        mensagem: string;
        stack?: string | null;
        url?: string | null;
        userAgent?: string | null;
        componentStack?: string | null;
        casoId?: string | null;
        criadoEm: string;
      }>;
    }>("GET", `/admin/client-errors?${qs.toString()}`);
  },

  /** Agregação dos erros JS por origem+mensagem nas últimas N horas.
   *
   * Backend retorna `ultima_em`/`primeira_em` em snake_case porque vêm direto
   * do agrupamento SQL (sem conversão). */
  adminClientErrorsAgregadas: (janelaHoras = 24) =>
    request<{
      janelaHoras: number;
      agregadas: Array<{
        origem: string;
        mensagem: string;
        fingerprint: string | null;   // PR #52 — null em rows antigas
        ocorrencias: number;
        ultima_em: string;
        primeira_em: string;
      }>;
    }>("GET", `/admin/client-errors/agregadas?janela_horas=${janelaHoras}`),

  /** Lista de TODOS os casos paginada (admin — PR #9/#13).
   *
   * Sem `pagina`/`porPagina` busca página 1 com 50 casos. Para paginação real,
   * passe os params explicitamente. Filtros opcionais por `vertical` e `etapa`.
   */
  casosTodos: async (
    opts?: { pagina?: number; porPagina?: number; vertical?: string; etapa?: string },
  ): Promise<{
    total: number;
    pagina: number;
    porPagina: number;
    casos: Array<{
      caseId: string;
      celularUltimos4: string;
      vertical: string;
      subtipo: string | null;
      etapa: string;
      valorEstimado: number | null;
      forcaCaso: string | null;
      criadoEm: string;
      atualizadoEm: string;
    }>;
  }> => {
    const qs = new URLSearchParams();
    qs.set("pagina", String(opts?.pagina ?? 1));
    qs.set("por_pagina", String(opts?.porPagina ?? 50));
    if (opts?.vertical) qs.set("vertical", opts.vertical);
    if (opts?.etapa) qs.set("etapa", opts.etapa);
    try {
      return await request("GET", `/admin/casos?${qs.toString()}`);
    } catch {
      return { total: 0, pagina: 1, porPagina: 50, casos: [] };
    }
  },

  // --- Chat in-app (PR #89) ---
  requestChat: (path: string) =>
    request<{
      mensagens: Array<{
        id: string;
        casoId: string;
        autor: "cliente" | "mediador";
        texto: string;
        criadoEm: string;
        lidaEm?: string | null;
      }>;
      total: number;
      naoLidasCliente: number;
      naoLidasMediador: number;
    }>("GET", path),

  enviarMensagemChat: (path: string, texto: string) =>
    request<{
      id: string;
      casoId: string;
      autor: "cliente" | "mediador";
      texto: string;
      criadoEm: string;
      lidaEm?: string | null;
    }>("POST", path, { texto }),

  // --- Admin config de fee (Onda 2) ---

  /** Lê a config de fee + piso vigentes (valores efetivos + fonte de cada um). */
  adminConfigFee: () => request<ConfigFee>("GET", "/admin/config/fee"),

  /** Grava override(s) de fee/piso. Campos omitidos não mudam. */
  adminAtualizarConfigFee: (input: {
    feePercentual?: number | null;
    feeMinimoCentavos?: number | null;
    valorMinimoAcordoReais?: number | null;
    custodyMode?: string | null;
    captadorFeePlataformaPct?: number | null;
    captadorFeeCaptadorPct?: number | null;
  }) => request<ConfigFee>("PUT", "/admin/config/fee", input),

  /** Cessionária (Art Produções): dados que entram no contrato/procuração + PIX. */
  adminConfigCessionario: () =>
    request<Cessionario>("GET", "/admin/config/cessionario"),
  adminAtualizarConfigCessionario: (input: Partial<Cessionario>) =>
    request<Cessionario>("PUT", "/admin/config/cessionario", input),

  /** Tabela de honorários por faixa (valores efetivos + flag de override). */
  adminConfigFaixas: () => request<ConfigFaixas>("GET", "/admin/config/faixas"),
  /** Grava overrides de % por faixa; `reset: true` volta aos defaults do código. */
  adminAtualizarConfigFaixas: (input: {
    faixas?: { indice: number; pctTotal: number; pctFee: number; pctHonorario: number }[];
    reset?: boolean;
  }) => request<ConfigFaixas>("PUT", "/admin/config/faixas", input),

  /** Tabela de honorários por faixa — PÚBLICA (reflete a config do admin). */
  tabelaHonorarios: () =>
    request<{
      faixas: {
        indice: number;
        rotulo: string;
        limiteInferior: string;
        limiteSuperior: string | null;
        pctTotal: string;
        pctFee: string;
        pctHonorario: string;
      }[];
    }>("GET", "/recuperacao/tabela-honorarios"),

  /** Mediadores — ficha CRUD (gestão admin). Esses nomes vão pra procuração. */
  adminMediadoresFichas: () =>
    request<{ total: number; mediadores: FichaMediador[] }>(
      "GET", "/admin/mediadores/fichas"),
  adminUpsertFichaMediador: (input: {
    chave: string; nomeCompleto: string; cpf: string;
    curso: string; instituicao: string; registro?: string | null;
  }) => request<FichaMediador>("POST", "/admin/mediadores/fichas", input),
  adminSetAtivoMediador: (chave: string, ativo: boolean) =>
    request<FichaMediador>(
      "PATCH",
      `/admin/mediadores/fichas/${encodeURIComponent(chave)}/ativo`,
      { ativo },
    ),

  /** Clientes (cadastro + PIX) agregados a partir dos casos. */
  adminClientes: () =>
    request<{ total: number; clientes: ClienteAdmin[] }>("GET", "/admin/clientes"),

  /** Ingere a resposta do réu (formulário-resposta) → registra na timeline. */
  ingerirRespostaReu: (casoId: string, texto: string) =>
    request<{
      ok: boolean;
      reconhecido: boolean;
      decisao: string | null;
      respostaId: string | null;
      parsed: Record<string, unknown>;
    }>("POST", `/admin/casos/${encodeURIComponent(casoId)}/resposta-reu`, { texto }),

  /** Perfil de qualificação do mediador LOGADO (auto-cadastro self-serve). */
  meuPerfilMediador: () =>
    request<FichaMediador>("GET", "/admin/mediadores/perfil"),
  salvarMeuPerfilMediador: (input: {
    nomeCompleto: string; cpf: string; curso: string; instituicao: string;
    registro?: string | null;
  }) => request<FichaMediador>("PUT", "/admin/mediadores/perfil", input),

  /** Gera (e persiste) a peça de defesa do caso (Onda 4.1). */
  adminGerarPecaDefesa: (casoId: string) =>
    request<{
      pecaId: string;
      casoId: string;
      texto: string;
      orgaoAlvo: string | null;
      fonte: string;
      citacoes: string[];
      geradaEm: string;
    }>("POST", `/admin/casos/${encodeURIComponent(casoId)}/peca-defesa`),

  /** Submete o caso a TODOS os órgãos de uma vez (Onda 4.2). */
  adminSubmeterConjunto: (casoId: string) =>
    request<{
      casoId: string;
      total: number;
      submissoes: Array<{ submissaoId: string; orgao: string; estado: string; protocolo: string | null }>;
    }>("POST", `/admin/casos/${encodeURIComponent(casoId)}/submeter-conjunto`),

  /** Respostas cumulativas do réu + melhor proposta financeira (Onda 4.3). */
  adminRespostasCaso: (casoId: string) =>
    request<{
      respostas: Array<{
        id: string;
        orgao: string | null;
        tipo: string;
        texto: string;
        valorContraproposta: number | null;
        escolhida: boolean;
        geradaEm: string;
      }>;
      melhorRespostaId: string | null;
      escolhidaId: string | null;
    }>("GET", `/admin/casos/${encodeURIComponent(casoId)}/respostas`),

  /** Mediador escolhe qual resposta seguir (Onda 4.3). */
  adminEscolherResposta: (casoId: string, respostaId: string) =>
    request<{ ok: boolean; escolhidaId: string }>(
      "POST",
      `/admin/casos/${encodeURIComponent(casoId)}/respostas/${encodeURIComponent(respostaId)}/escolher`,
    ),
};

export default api;
